import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, UserCheck, CheckCircle2,
  RotateCcw, Clock, MessageSquare, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  SUBMITTED: "bg-slate-100 text-slate-700",
  ACKNOWLEDGED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  REOPENED: "bg-red-100 text-red-700",
};

const VALID_TRANSITIONS = {
  SUBMITTED: ["ACKNOWLEDGED", "IN_PROGRESS"],
  ACKNOWLEDGED: ["IN_PROGRESS"],
  IN_PROGRESS: ["COMPLETED"],
  COMPLETED: ["REOPENED"],
  REOPENED: ["IN_PROGRESS"],
};

export default function RequestServiceRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [request, setRequest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignTo, setAssignTo] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDetail = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [reqRes, histRes] = await Promise.all([
        axios.get(`${API}/request/requests/${id}`, { headers }),
        axios.get(`${API}/request/requests/${id}/history`, { headers }),
      ]);
      setRequest(reqRes.data);
      setHistory(histRes.data || []);
    } catch (err) {
      toast.error("Failed to load request");
      navigate("/request-service/my-requests");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleAssign = async () => {
    if (!assignTo) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/request/requests/${id}/assign`,
        { assigned_to: assignTo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Request assigned successfully");
      setAssignTo("");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
    setActionLoading(false);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const body = { status: newStatus };
      if (newStatus === "REOPENED") body.reopen_reason = reopenReason;
      await axios.put(`${API}/request/requests/${id}/status`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
      setNewStatus("");
      setReopenReason("");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
    setActionLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!request) return null;

  const currentStatus = request.status;
  const possibleNextStatuses = VALID_TRANSITIONS[currentStatus] || [];
  const userRole = auth.user?.role;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate("/request-service/my-requests")}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to My Requests</span>
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  STATUS_STYLES[currentStatus]
                }`}
              >
                {currentStatus?.replace("_", " ")}
              </span>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                {request.priority}
              </span>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                {request.department}
              </span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800">
              {request.service_type}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Requested by</p>
            <p className="font-medium text-slate-800">{request.requested_by_name || request.requested_by}</p>
          </div>
          <div>
            <p className="text-slate-500">Assigned to</p>
            <p className="font-medium text-slate-800">{request.assigned_to_name || request.assigned_to || "Unassigned"}</p>
          </div>
          <div>
            <p className="text-slate-500">Created</p>
            <p className="font-medium text-slate-800">
              {request.created_at ? new Date(request.created_at).toLocaleString() : "-"}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Completed</p>
            <p className="font-medium text-slate-800">
              {request.completed_at ? new Date(request.completed_at).toLocaleString() : "-"}
            </p>
          </div>
        </div>

        {request.description && (
          <div className="mt-4 p-4 bg-app rounded-lg">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{request.description}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Assign */}
        {userRole === "admin" && !request.assigned_to && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <UserCheck className="w-4 h-4" /> Assign Request
            </h3>
            <div className="flex gap-2">
              <Input
                placeholder="Enter user ID to assign"
                value={assignTo}
                onChange={(e) => setAssignTo(e.target.value)}
              />
              <Button
                onClick={handleAssign}
                disabled={actionLoading || !assignTo}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign"}
              </Button>
            </div>
          </div>
        )}

        {/* Status Update */}
        {possibleNextStatuses.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" /> Update Status
            </h3>
            <div className="space-y-3">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select next status" />
                </SelectTrigger>
                <SelectContent>
                  {possibleNextStatuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === "REOPENED" ? "Reopen Request" : s.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newStatus === "REOPENED" && (
                <Textarea
                  placeholder="Reason for reopening..."
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  rows={2}
                />
              )}
              <Button
                onClick={handleStatusUpdate}
                disabled={actionLoading || !newStatus}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Update Status
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Request Timeline
        </h3>
        {history.length === 0 ? (
          <p className="text-slate-500 text-sm">No history entries yet.</p>
        ) : (
          <div className="space-y-3">
            {history.map((entry, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0"
              >
                <div className="w-2 h-2 mt-2 rounded-full bg-teal-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">{entry.updated_by}</span>
                    {entry.changes?.status && (
                      <> changed status to <span className="font-medium">{entry.changes.status}</span></>
                    )}
                    {entry.changes?.assigned_to && (
                      <> assigned to {entry.changes.assigned_to}</>
                    )}
                    {entry.changes?.comment && (
                      <> — {entry.changes.comment}</>
                    )}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
